import { Group } from './group.model';
import { PasswordProtection } from './password-protection.model';
import { CustomizedBlockPage } from './customized-block-page.model';
import { OptInPreference } from '@/constants/features/opt-in';

export enum Duration {
  MONTHLY = 'monthly',
  SEMI_ANNUAL = 'semi_annual',
  ANNUAL = 'annual',
  LIFETIME = 'lifetime',
}

type PremiumTypes = 'subscription' | 'purchase';

export interface IPremiumDetail {
  type: PremiumTypes;
  trialPeriod: number;
  isTrialActive: boolean;
  nextBillingTime: string;
  isCancelled: boolean;
  duration?: Duration;
  provider: string;
  planPrice: number;
  currency: string;
  country: string;
  createdAt?: string;
}

export interface IPremiumDetailsResponse {
  isPremium: boolean;
  premiumDetails?: IPremiumDetail;
}

export interface IUser {
  uid: string;
  email: string;
  phoneNumber: string;
  displayName: string;
  photoURL: string;
  isAnonymous: boolean;
  isPremium: boolean;
  premiumDetails?: IPremiumDetail | null;
}

export const emptyUser: IUser = {
  uid: '',
  email: '',
  phoneNumber: '',
  displayName: '',
  photoURL: '',
  isAnonymous: true,
  isPremium: false,
};

export interface UserConfig {
  groups: Group[];
  passwordProtection: PasswordProtection;
  customizedBlockPage: CustomizedBlockPage;
  flagsConfig: IFlagsConfig;
}

export enum EUserAvatarSize {
  small = 'small',
  medium = 'medium',
  large = 'large',
  extraSmall = 'extraSmall',
}

export const FLAG_CONFIG_KEYS = {
  IS_PRIVACY_AGREED: 'isPrivacyAgreed',
  IS_BLOCKING_ENABLE: 'isBlockingEnable',
  IS_CONTEXT_MENUS_ENABLE: 'isContextMenusEnable',
  SHOW_MOTIVATIONAL_IMAGES: 'showMotivationalImages',
  SHOW_WORK_MODE_TIME_FAV_ICON: 'showWorkModeTimeFavIcon',
  MANDATORY_BUTTON_CLICKED: 'mandatoryButtonClicked',
  OPT_IN_PREFERENCES: 'optInPreferences',
  EULA_AGREED: 'eulaAgreed',
  SAW_BUCKETS_OPT_IN_ONBOARDING_SCREEN: 'sawBucketsOptInOnboardingScreen',
  RECONSENT_BANNER_DISMISSED: 'reconsentBannerDismissed',
  ON_NEW_OPT_IN_SCHEMA: 'onNewOptInSchema',
  CONSENT_AFFILIATES: 'consentAffiliates',
  CONSENT_THIRD_PARTIES: 'consentThirdParties',
} satisfies Record<string, keyof IFlagsConfig>;

// IFlagsConfig holds all local flags; only these keys are synced with the server.
export const SERVER_SYNCED_FLAG_KEYS: (keyof IFlagsConfig)[] = [
  FLAG_CONFIG_KEYS.IS_BLOCKING_ENABLE,
  FLAG_CONFIG_KEYS.IS_CONTEXT_MENUS_ENABLE,
  FLAG_CONFIG_KEYS.IS_PRIVACY_AGREED,
  FLAG_CONFIG_KEYS.SHOW_MOTIVATIONAL_IMAGES,
  FLAG_CONFIG_KEYS.SHOW_WORK_MODE_TIME_FAV_ICON,
  FLAG_CONFIG_KEYS.MANDATORY_BUTTON_CLICKED,
  FLAG_CONFIG_KEYS.OPT_IN_PREFERENCES,
  FLAG_CONFIG_KEYS.EULA_AGREED,
  FLAG_CONFIG_KEYS.SAW_BUCKETS_OPT_IN_ONBOARDING_SCREEN,
  FLAG_CONFIG_KEYS.RECONSENT_BANNER_DISMISSED,
  FLAG_CONFIG_KEYS.ON_NEW_OPT_IN_SCHEMA,
  FLAG_CONFIG_KEYS.CONSENT_AFFILIATES,
  FLAG_CONFIG_KEYS.CONSENT_THIRD_PARTIES,
];

// Consent choices carried across login and reset on logout. Schema and
// onboarding-seen markers are not here — they are not consent and must survive logout.
export const CONSENT_FLAG_KEYS: (keyof IFlagsConfig)[] = [
  FLAG_CONFIG_KEYS.IS_PRIVACY_AGREED,
  FLAG_CONFIG_KEYS.MANDATORY_BUTTON_CLICKED,
  FLAG_CONFIG_KEYS.OPT_IN_PREFERENCES,
  FLAG_CONFIG_KEYS.EULA_AGREED,
  FLAG_CONFIG_KEYS.RECONSENT_BANNER_DISMISSED,
  FLAG_CONFIG_KEYS.CONSENT_AFFILIATES,
  FLAG_CONFIG_KEYS.CONSENT_THIRD_PARTIES,
];

export type ServerSyncedFlagsConfig = Pick<
  IFlagsConfig,
  (typeof SERVER_SYNCED_FLAG_KEYS)[number]
>;

export interface SaveUserConfigPayload {
  groups?: Group[];
  passwordProtection?: PasswordProtection;
  customizedBlockPage?: CustomizedBlockPage;
  flagsConfig?: Partial<ServerSyncedFlagsConfig>;
}

export interface IFlagsConfig {
  isBlockingEnable: boolean;
  isContextMenusEnable: boolean;
  isPrivacyAgreed: boolean;
  showMotivationalImages: boolean;
  showWorkModeTimeFavIcon: boolean;
  slackDNDFocusMode: boolean;
  mandatoryButtonClicked: boolean;
  isExtensionSyncEnable: boolean;
  showSyncStatus: boolean;
  showPremiumOverlay: boolean;
  showWhatsNewOverlay: boolean;
  dontShowSlackFeatureOverlay: boolean;
  showAddSiteOverlay: boolean;
  displayPinToToolbarMessage: boolean;
  // Source of truth for clickStream opt-in (new schema only); isPrivacyAgreed mirrors includes('cs').
  optInPreferences?: OptInPreference[];
  // Explicit new-schema marker; written by the extension (migration/install). Web-app syncs and reads it only.
  onNewOptInSchema?: boolean;
  eulaAgreed?: boolean;
  // Did the user see the new opt-in screen; written by web-app.
  sawBucketsOptInOnboardingScreen?: boolean;
  // Banner permanently dismissed; does not affect collection, only silences the nag.
  reconsentBannerDismissed?: boolean;
  // RoW disclosure acceptances. Sticky-true: the consent modal only ever ORs
  // them in, so an already-accepted disclosure row is hidden on later visits.
  consentAffiliates?: boolean;
  consentThirdParties?: boolean;
  // Monitoring-only marker; written by the extension migration, web-app reads only.
  optInBucketsMigrationCompleted?: boolean;
}
